"use client";

import type { KundaliResponse } from "@/lib/api/kundali";
import { ValueMomentContactCapture } from "@/components/ValueMomentContactCapture";
import {
  ToolResultChatBridge,
  type ToolResultChatBridgeCopy,
} from "@/components/tool-shell/ToolResultChatBridge";
import { resolveRashi } from "@/lib/rashi-presentation";
import { nakshatraNameHi } from "@/lib/vedic-names-hi";

/** Opt-in placement id: `consent_placement` on the ADR-0043 audit row and the
 * `placement` on contact_capture_shown / contact_captured.
 * @internal
 */
export const RASHI_COMPACT_VALUE_MOMENT_PLACEMENT = "rashi-result-compact-value-moment";
/**
 * Chat-bridge placement id on seo_agent_bridge_viewed / _clicked.
 * @internal
 */
export const RASHI_CHAT_BRIDGE_PLACEMENT = "rashi_result_chat_bridge";

const BRIDGE_COPY: Record<"en" | "hi", ToolResultChatBridgeCopy> = {
  en: {
    eyebrow: "Your Moon sign is ready",
    headline: "Ask what this rashi means for your life",
    subhead:
      "Pandit-ji opens this same chart. Ask about career, marriage or timing, with no details to re-enter.",
    cta: "Ask Pandit-ji about my rashi",
  },
  hi: {
    eyebrow: "आपकी राशि तैयार है",
    headline: "यह राशि आपके जीवन के बारे में क्या कहती है?",
    subhead:
      "Pandit-ji yahi kundali kholenge. Career, vivah ya timing par poochhein, details dobara bharne ki zaroorat nahi.",
    cta: "Pandit-ji se meri rashi poochhein",
  },
};

/**
 * The first chat message, built from what the engine computed (rule 29: the
 * facts come from the result, the seeker only asks). Null when the result has
 * no readable rashi, so no bridge is shown for an unreadable chart.
 * @internal
 */
export function rashiChatPrompt(
  result: Pick<KundaliResponse, "moon_rashi" | "moon_nakshatra">,
  pada: number | string,
  language: "en" | "hi"
): string | null {
  const rashi = resolveRashi(result.moon_rashi);
  if (!rashi) return null;
  const hasPada = pada !== "-" && pada !== "";
  if (language === "hi") {
    const nakshatra = nakshatraNameHi(result.moon_nakshatra) ?? result.moon_nakshatra;
    const padaText = hasPada ? ` (pada ${pada})` : "";
    return `Meri chandra rashi ${rashi.name_hi} hai aur nakshatra ${nakshatra}${padaText}. Isse mere swabhav ke baare mein kya pata chalta hai, aur abhi mujhe kis par dhyan dena chahiye?`;
  }
  const nakshatra = result.moon_nakshatra.replace(/_/g, " ");
  const padaText = hasPada ? `, pada ${pada}` : "";
  return `My Moon sign is ${rashi.name_en} (${rashi.name_sa}) with ${nakshatra} nakshatra${padaText}. What does this say about my nature, and what should I focus on now?`;
}

/**
 * The two recovery levers on the rashi result (readout 2026-10-27), shown only
 * after the answer and after the page's single report offer (rule 39):
 *  1. the ADR-0043 strong-consent opt-in, compact variant, for THIS reading —
 *     the same audited copy and copy_version as the kundali result card;
 *  2. the chat bridge into this reading's AI astrologer chat.
 * Both render in the same commit as the result inside the rule-58 reserve, so
 * nothing is inserted later above content the seeker can see.
 */
export function RashiResultLevers({
  result,
  pada,
  language,
}: {
  result: KundaliResponse;
  pada: number | string;
  language: "en" | "hi";
}) {
  const prompt = rashiChatPrompt(result, pada, language);
  if (!result.id || !prompt) return null;
  return (
    <div className="mt-4" data-rashi-result-levers>
      <ValueMomentContactCapture
        target={{ type: "reading", id: result.id }}
        language={language}
        placement={RASHI_COMPACT_VALUE_MOMENT_PLACEMENT}
        variant="compact"
      />
      <ToolResultChatBridge
        language={language}
        sourcePath={language === "hi" ? "/hi/rashi-calculator" : "/rashi-calculator"}
        placement={RASHI_CHAT_BRIDGE_PLACEMENT}
        prompt={prompt}
        copy={BRIDGE_COPY[language]}
        presentationKey={result.id}
        readingId={result.id}
      />
    </div>
  );
}
